import React from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { subCategoryDisplayLabel } from './subCategoryLabels';

interface OptionGridProps {
  options: string[];
  /** Selected values. Single-select steps pass at most one. */
  selectedOptions: string[];
  onSelect: (option: string) => void;
  /** OV-1180: render checkboxes and let the user pick several. */
  allowMultiple?: boolean;
  /** Options that cannot be combined with the current selection. */
  disabledOptions?: string[];
}

const OptionGrid: React.FC<OptionGridProps> = ({
  options,
  selectedOptions,
  onSelect,
  allowMultiple = false,
  disabledOptions = [],
}) => {
  const isSelected = (option: string) => selectedOptions.includes(option);
  const isDisabled = (option: string) => disabledOptions.includes(option);

  const getLabelClasses = (option: string) => {
    const optionClasses =
      'flex items-center p-4 border rounded-lg transition-colors group min-h-[60px]';
    const selectedOptionClasses =
      'bg-primary-50 border-primary-500 hover:bg-primary-100 cursor-pointer';
    const unselectedOptionClasses =
      'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 cursor-pointer';
    const disabledOptionClasses =
      'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed';

    if (isDisabled(option)) return `${optionClasses} ${disabledOptionClasses}`;

    return `${optionClasses} ${
      isSelected(option) ? selectedOptionClasses : unselectedOptionClasses
    }`;
  };

  const getTextClasses = (option: string) => {
    if (isDisabled(option)) return 'text-gray-400';

    return isSelected(option)
      ? 'text-primary-700 font-medium'
      : 'text-gray-700';
  };

  const handleSelect = (option: string) => {
    if (isDisabled(option)) return;
    onSelect(option);
  };

  if (allowMultiple) {
    return (
      <div className="grid grid-cols-3 gap-4" role="group">
        {options.map(option => (
          <Label
            htmlFor={option}
            key={option}
            className={getLabelClasses(option)}
          >
            <Checkbox
              id={option}
              checked={isSelected(option)}
              disabled={isDisabled(option)}
              onCheckedChange={() => handleSelect(option)}
              className="mr-3 cursor-pointer"
            />
            <span className={`${getTextClasses(option)} leading-relaxed`}>
              {subCategoryDisplayLabel(option)}
            </span>
          </Label>
        ))}
      </div>
    );
  }

  return (
    <RadioGroup className="grid grid-cols-3 gap-4">
      {options.map(option => (
        <Label
          htmlFor={option}
          key={option}
          className={getLabelClasses(option)}
        >
          <RadioGroupItem
            id={option}
            value={option}
            checked={isSelected(option)}
            disabled={isDisabled(option)}
            onClick={() => handleSelect(option)}
            className="mr-3 cursor-pointer"
          />
          <span className={`${getTextClasses(option)} leading-relaxed`}>
            {subCategoryDisplayLabel(option)}
          </span>
        </Label>
      ))}
    </RadioGroup>
  );
};

export default OptionGrid;
